/**
 * Message Deduplication Utilities
 * Based on MESSAGE_DEDUP_AND_REALTIME_GUIDE.md
 */

import { createHash } from 'crypto';

export interface MessageHash {
  contentHash: string;
  timestamp: number;
  messageId: string;
}

/**
 * Generate SHA-256 hash of message content for deduplication
 */
export function generateMessageHash(content: string): string {
  // Browser-compatible hash generation
  const encoder = new TextEncoder();
  const data = encoder.encode(content.trim().toLowerCase());
  
  // Simple hash for browser (not crypto-secure but sufficient for dedup)
  let hash = 0;
  const bytes = new Uint8Array(data);
  for (let i = 0; i < bytes.length; i++) {
    hash = ((hash << 5) - hash) + bytes[i];
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
}

/**
 * Check if message is duplicate based on content hash and time window
 */
export function isDuplicateMessage(
  newHash: string,
  existingHashes: Map<string, number>,
  timeWindowMs: number = 5000
): boolean {
  const now = Date.now();
  const existingTimestamp = existingHashes.get(newHash);
  
  if (!existingTimestamp) {
    return false;
  }
  
  // Check if within time window
  return (now - existingTimestamp) < timeWindowMs;
}

/**
 * Clean expired hashes from the map
 */
export function cleanExpiredHashes(
  hashes: Map<string, number>,
  expirationMs: number = 60000 // 1 minute
): void {
  const now = Date.now();
  const expiredKeys: string[] = [];
  
  hashes.forEach((timestamp, hash) => {
    if (now - timestamp > expirationMs) {
      expiredKeys.push(hash);
    }
  });
  
  expiredKeys.forEach(key => hashes.delete(key));
}

/**
 * Deduplication manager class
 */
export class MessageDeduplicationManager {
  private contentHashes: Map<string, number> = new Map();
  private cleanupInterval: number | null = null;
  
  constructor(
    private timeWindowMs: number = 5000,
    private cleanupIntervalMs: number = 30000
  ) {
    this.startCleanup();
  }
  
  /**
   * Check if message is duplicate and track it
   */
  checkAndTrack(content: string): boolean {
    const hash = generateMessageHash(content);
    const isDuplicate = isDuplicateMessage(hash, this.contentHashes, this.timeWindowMs);
    
    if (!isDuplicate) {
      this.contentHashes.set(hash, Date.now());
    }
    
    return isDuplicate;
  }
  
  /**
   * Start periodic cleanup
   */
  private startCleanup(): void {
    if (this.cleanupInterval) return;
    
    this.cleanupInterval = window.setInterval(() => {
      cleanExpiredHashes(this.contentHashes);
    }, this.cleanupIntervalMs);
  }
  
  /**
   * Stop cleanup and clear hashes
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.contentHashes.clear();
  }
  
  /**
   * Get current hash count for debugging
   */
  getHashCount(): number {
    return this.contentHashes.size;
  }
}